import { queryOptions, useQuery } from '@tanstack/react-query';

import { api } from 'lib/api-client';
import { QueryConfig } from 'lib/react-query';
import { DatasetGroup } from 'types/api';

type GetDatasetGroupResponse = {
    dataset_group: DatasetGroup;
};

export type GetDatasetGroupQueryParams = {
    dataset_id: string;
};

export type GetDatasetGroupOptions = {
    id?: string;
    params?: GetDatasetGroupQueryParams;
};

export const getDatasetGroup = async (
    options: GetDatasetGroupOptions,
): Promise<DatasetGroup> => {
    if (options.id) {
        const response = await api.get<
            GetDatasetGroupResponse,
            GetDatasetGroupResponse
        >(`/app/api/v1/dataset_group/${options.id}`);
        return response.dataset_group;
    } else if (options.params) {
        const response = await api.get<
            GetDatasetGroupResponse,
            GetDatasetGroupResponse
        >('/app/api/v1/dataset_group', { params: options.params });
        return response.dataset_group;
    }

    throw new Error(
        "Either 'id' or 'params' must be provided to fetch a dataset group.",
    );
};

export const getDatasetGroupQueryOptions = (
    options: GetDatasetGroupOptions,
) => {
    return queryOptions({
        queryKey: options.params
            ? ['dataset_group', { params: options.params }]
            : ['dataset_group', options.id],
        queryFn: () => getDatasetGroup(options),
    });
};

type UseDatasetGroupOptions =
    | {
          id: string;
          params?: never;
          queryConfig?: QueryConfig<typeof getDatasetGroupQueryOptions>;
      }
    | {
          id?: never;
          params: GetDatasetGroupQueryParams;
          queryConfig?: QueryConfig<typeof getDatasetGroupQueryOptions>;
      };

export const useDatasetGroup = (options: UseDatasetGroupOptions) => {
    return useQuery({
        ...getDatasetGroupQueryOptions(options),
        ...options.queryConfig,
    });
};
